import dynamic from "next/dynamic";
import type { FC } from "react";
import useSiftedMediaQueries from "lib/hooks/useSiftedMediaQueries";
import { useHomepageDatoProps } from "lib/store/homepage";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import Image from "components/imageNew/image";
import HomePageSectionHeader from "components/homePageSectionHeader/homePageSectionHeader";
import { LinkButton } from "components/simpleButton/linkButton";
import type { HomepageDatoProps, EventsCarouselItem } from "lib/types/homepage";

const LinkedImage = ({
  imageData,
  mobile,
}: {
  imageData: any;
  mobile: boolean;
}) => {
  return (
    <>
      {mobile ? (
        <Image
          width={imageData.mobileImage.width}
          height={imageData.mobileImage.height}
          src={imageData.mobileImage.url}
          alt={imageData.mobileImage.alt || ""}
          blurDataURL={imageData.mobileImage.blurUpThumb}
          className="pointer-events-none h-full w-full object-cover"
          quality={80}
          sizes="400px"
        />
      ) : (
        <Image
          width={imageData.desktopTabletImage.width}
          height={imageData.desktopTabletImage.height}
          src={imageData.desktopTabletImage.url}
          alt={imageData.desktopTabletImage.alt || ""}
          blurDataURL={imageData.desktopTabletImage.blurUpThumb}
          className="pointer-events-none h-full w-full object-cover"
          quality={80}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1220px"
        />
      )}
    </>
  );
};

interface EventsContentProps {
  event: EventsCarouselItem;
  headerTitle?: string;
  headerCtaUrl?: string;
  headerCtaText?: string;
  eventsCtaUrl?: string;
  eventsCtaText?: string;
  descriptionTitle?: string;
  eventsDescription?: string;
}

const EventsContent: FC<EventsContentProps> = ({
  event,
  headerTitle,
  headerCtaUrl,
  headerCtaText,
  eventsCtaUrl,
  eventsCtaText,
  descriptionTitle,
  eventsDescription,
}) => {
  const { track } = useMixpanelTracking();
  const { mobile } = useSiftedMediaQueries();

  const title = headerTitle;
  const description = eventsDescription;
  const buttonText = eventsCtaText;
  const descTitle = descriptionTitle;

  return (
    <div className="flex flex-col gap-4 pb-16">
      {/* Section Header */}
      <HomePageSectionHeader
        title={title}
        buttonText={headerCtaText}
        buttonUrl={headerCtaUrl || event.url}
        onButtonClick={async () => {
          await track("Events header click", {
            "Event URL": event.url,
          });
        }}
      />

      {/* 2 Column Layout - Stacks on mobile, side-by-side on tablet+ */}
      {/* Limit the row height on tablet+ (desktop) to 260px as requested */}
      <div className="flex w-full flex-col items-start gap-4 md:max-h-[260px] md:flex-row md:overflow-hidden mdDesktop:max-h-[260px] mdDesktop:flex-row mdDesktop:overflow-hidden">
        {/* Image column */}
        <div className="h-[138px] w-full flex-1 overflow-hidden md:h-[260px] mdDesktop:h-[260px]">
          <LinkedImage imageData={event} mobile={mobile} />
        </div>

        {/* Content column */}
        <div className="flex w-full flex-1 flex-col gap-6 md:h-[260px] md:justify-center mdDesktop:h-[260px] mdDesktop:justify-center">
          {/* Text content with 10px gap */}
          <div className="flex flex-col gap-[10px]">
            {/* Heading */}
            {(descTitle || title) && (
              <h2 className="font-recklessBold text-accent-26 text-brand-neutral">
                {descTitle || title}
              </h2>
            )}

            {/* Description */}
            {description && (
              <p className="text-assist-14 leading-tight text-brand-neutral">
                {description}
              </p>
            )}
          </div>

          {/* Button */}
          {buttonText && eventsCtaUrl && (
            <LinkButton
              href={eventsCtaUrl}
              onClick={async () => {
                await track("Events button click", {
                  "Event URL": eventsCtaUrl,
                });
              }}
              variant="primary"
              accent="sifted"
              size="lg"
              className="ga-event-link w-fit"
            >
              {buttonText}
            </LinkButton>
          )}
        </div>
      </div>
    </div>
  );
};

export const Events = () => {
  const datoProps: HomepageDatoProps = useHomepageDatoProps();

  // API always returns a single event object
  const event = datoProps.events as unknown as EventsCarouselItem | undefined;

  if (!event) return null;

  const { header, eventsCta, descriptionTitle, eventsDescription } = datoProps;

  return (
    <EventsContent
      event={event}
      headerTitle={header?.title}
      headerCtaUrl={header?.cta?.url}
      headerCtaText={header?.cta?.text}
      eventsCtaUrl={eventsCta?.url}
      eventsCtaText={eventsCta?.text}
      descriptionTitle={descriptionTitle}
      eventsDescription={eventsDescription}
    />
  );
};

// Ensure this component only renders on the client (no SSR)
export default dynamic(() => Promise.resolve(Events), { ssr: false });
